import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Wallet, Plus, Upload, Loader2, Trash2, Check, Link2, RefreshCw, Unplug, Settings2, AlertTriangle, TrendingUp, TrendingDown, Landmark, Hourglass, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Panel, StatusBadge } from "@/components/common";
import { UpgradeGate } from "@/components/UpgradeGate";
import { DatePicker } from "@/components/DatePicker";
import AdvisorChat from "@/components/AdvisorChat";
import { api, errMsg, localToday, fmtDate, fmtDateTime, BACKEND_URL } from "@/lib/api";
import { useAuth, isManager } from "@/context/AuthContext";

const money = (v, cur = "USD") => {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0); }
  catch { return `${cur} ${Math.round(v || 0).toLocaleString()}`; }
};
const monthLabel = (m) => new Date(`${m}-01T00:00`).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
const lastMonths = () => [...Array(12)].map((_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });

const Kpi = ({ label, value, sub, icon: Icon, tone, testid }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between"><p className="text-xs text-muted-foreground">{label}</p><Icon className={`h-4 w-4 ${tone || "text-muted-foreground"}`} /></div>
    <p className="text-xl sm:text-2xl font-semibold font-display tabular-nums mt-2" data-testid={`${testid}-value`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

function Overview({ s, reload, manager }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ currency: s.currency, opening_cash: s.opening_cash });
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch("/finance/settings", { currency: f.currency, opening_cash: Number(f.opening_cash) }); toast.success("Saved"); setOpen(false); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const cur = s.currency;
  const tm = s.this_month;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Cash balance" value={money(s.cash_balance, cur)} icon={Landmark} testid="cfo-kpi-cash" />
        <Kpi label="Runway" value={s.runway_months ? `${s.runway_months} mo` : "Profitable"} sub={s.avg_monthly_burn ? `Burn ${money(s.avg_monthly_burn, cur)}/mo` : "No net burn (last 3 mo)"} icon={Hourglass} tone={s.runway_months && s.runway_months < 6 ? "text-destructive" : ""} testid="cfo-kpi-runway" />
        <Kpi label="Revenue this month" value={money(tm.income, cur)} sub={s.revenue_change_pct != null ? `${s.revenue_change_pct > 0 ? "+" : ""}${s.revenue_change_pct}% vs last month` : ""} icon={TrendingUp} testid="cfo-kpi-revenue" />
        <Kpi label="Expenses this month" value={money(tm.expense, cur)} icon={TrendingDown} testid="cfo-kpi-expenses" />
        <Kpi label="Net this month" value={money(tm.net, cur)} icon={Wallet} tone={tm.net < 0 ? "text-destructive" : "text-[hsl(var(--success))]"} testid="cfo-kpi-net" />
        <Kpi label="Receivables" value={money(s.receivables, cur)} sub={`${s.open_invoices} open invoices`} icon={Receipt} testid="cfo-kpi-receivables" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel title="Income vs expenses (6 months)" className="lg:col-span-8" testid="cfo-chart"
          action={manager && <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid="cfo-settings-button"><Settings2 className="h-4 w-4 mr-1" />Currency & opening cash</Button>}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.monthly.map((m) => ({ ...m, label: monthLabel(m.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(42 18% 86%)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={40} />
                <Tooltip formatter={(v) => money(v, cur)} />
                <Legend />
                <Bar dataKey="income" name="Income" fill="hsl(158 52% 38%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Expenses" fill="hsl(210 78% 38%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Overdue invoices" icon={AlertTriangle} className="lg:col-span-4" testid="cfo-overdue">
          {s.overdue_invoices.length === 0 ? <p className="text-sm text-muted-foreground">Nothing overdue.</p> : (
            <ul className="divide-y divide-border">
              {s.overdue_invoices.map((i) => (
                <li key={i.invoice_id} className="py-2.5 flex justify-between gap-2 text-sm">
                  <div><p className="font-medium">{i.customer}</p><p className="text-xs text-destructive">{i.number} · due {fmtDate(i.due_date)}</p></div>
                  <span className="tabular-nums font-medium">{money(i.amount, cur)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <Panel title="Budget vs actual this month" testid="cfo-budget-overview">
        {s.budget_vs_actual.length === 0 ? <p className="text-sm text-muted-foreground">No expenses or budgets yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {s.budget_vs_actual.map((b) => {
              const pct = b.budget ? Math.min(100, Math.round((b.actual / b.budget) * 100)) : 0;
              const over = b.budget && b.actual > b.budget;
              return (
                <div key={b.category}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="font-medium">{b.category}</span>
                    <span className={`tabular-nums ${over ? "text-destructive font-medium" : "text-muted-foreground"}`}>{money(b.actual, cur)}{b.budget ? ` / ${money(b.budget, cur)}` : " · no budget"}</span></div>
                  <Progress value={b.budget ? pct : 0} className={`h-2 ${over ? "[&>div]:bg-destructive" : ""}`} />
                </div>
              );
            })}
          </div>
        )}
      </Panel>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-sm" data-testid="cfo-settings-dialog">
          <DialogHeader><DialogTitle>Finance settings</DialogTitle><DialogDescription>Cash balance = opening cash + all income − all expenses.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Currency code</Label><Input maxLength={3} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} placeholder="USD, GBP, EUR, INR" data-testid="cfo-currency-input" /></div>
            <div className="space-y-1.5"><Label>Opening cash balance</Label><Input type="number" step="0.01" value={f.opening_cash} onChange={(e) => setF({ ...f, opening_cash: e.target.value })} data-testid="cfo-opening-cash-input" /></div>
            <DialogFooter><Button type="submit" data-testid="cfo-settings-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Transactions({ cur, manager, onChange }) {
  const [month, setMonth] = useState(lastMonths()[0]);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ type: "expense", amount: "", date: localToday(), category: "", description: "", counterparty: "" });
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/finance/transactions", { params: { month } }); setRows(data); } catch (e) { toast.error(errMsg(e)); setRows([]); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/finance/transactions", { ...f, amount: Number(f.amount) }); toast.success("Transaction added"); setOpen(false); setF({ ...f, amount: "", description: "" }); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const del = async (t) => { try { await api.delete(`/finance/transactions/${t.txn_id}`); load(); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/finance/transactions/import", fd);
      toast.success(`Imported ${data.imported} transactions`, { description: data.skipped ? `${data.skipped} rows skipped. ${data.errors.join(" ")}` : undefined });
      load(); onChange();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const inc = (rows || []).filter((r) => r.type === "income").reduce((a, r) => a + r.amount, 0);
  const exp = (rows || []).filter((r) => r.type === "expense").reduce((a, r) => a + r.amount, 0);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-40 bg-card" data-testid="txn-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{lastMonths().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">In <b className="text-foreground tabular-nums">{money(inc, cur)}</b> · Out <b className="text-foreground tabular-nums">{money(exp, cur)}</b></span>
        {manager && (
          <div className="ml-auto flex gap-2">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="txn-import-input" />
            <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="txn-import-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}Import CSV</Button>
            <Button onClick={() => setOpen(true)} data-testid="txn-add-button"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-3">CSV import reads a Date column plus either Amount (negative means an expense) or Debit/Credit columns. Description and Category columns are optional.</p>
      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        {rows === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : rows.length === 0 ? <EmptyState icon={Wallet} title="No transactions this month" /> : (
          <Table data-testid="txn-table">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="hidden md:table-cell">Category</TableHead><TableHead className="hidden md:table-cell">Source</TableHead><TableHead className="text-right">Amount</TableHead>{manager && <TableHead className="w-10" />}</TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.txn_id} data-testid="txn-row">
                  <TableCell className="tabular-nums whitespace-nowrap">{fmtDate(t.date)}</TableCell>
                  <TableCell><p className="text-sm">{t.description || "—"}</p>{t.counterparty && <p className="text-xs text-muted-foreground">{t.counterparty}</p>}</TableCell>
                  <TableCell className="hidden md:table-cell">{t.category}</TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground capitalize">{t.source}</TableCell>
                  <TableCell className={`text-right tabular-nums font-medium whitespace-nowrap ${t.type === "income" ? "text-[hsl(var(--success))]" : ""}`}>{t.type === "income" ? "+" : "−"}{money(t.amount, cur)}</TableCell>
                  {manager && <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(t)} aria-label="Delete" data-testid="txn-delete-button"><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="txn-dialog">
          <DialogHeader><DialogTitle>Add transaction</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="txn-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="income">Income</SelectItem><SelectItem value="expense">Expense</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Amount ({cur})</Label><Input required type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="txn-amount-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Date</Label><DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="txn-date-picker" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Category</Label><Input required value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="e.g. Software" data-testid="txn-category-input" /></div>
              <div className="space-y-1.5"><Label>Customer / supplier</Label><Input value={f.counterparty} onChange={(e) => setF({ ...f, counterparty: e.target.value })} data-testid="txn-counterparty-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="txn-description-input" /></div>
            <DialogFooter><Button type="submit" disabled={busy} data-testid="txn-save-button">Add transaction</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Invoices({ cur, manager, onChange }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer: "", amount: "", due_date: "", description: "", status: "sent" });
  const load = useCallback(async () => { try { const { data } = await api.get("/finance/invoices", { params: { today: localToday() } }); setRows(data); } catch (e) { toast.error(errMsg(e)); setRows([]); } }, []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/finance/invoices", { ...f, amount: Number(f.amount), due_date: f.due_date || null }); toast.success("Invoice created"); setOpen(false); setF({ customer: "", amount: "", due_date: "", description: "", status: "sent" }); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const paid = async (i) => { try { await api.post(`/finance/invoices/${i.invoice_id}/paid`); toast.success(`${i.number} marked paid`); load(); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  const del = async (i) => { if (!window.confirm(`Delete ${i.number}?`)) return; try { await api.delete(`/finance/invoices/${i.invoice_id}`); load(); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div>
      {manager && <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} data-testid="invoice-add-button"><Plus className="h-4 w-4 mr-1.5" />New invoice</Button></div>}
      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        {rows === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : rows.length === 0 ? <EmptyState icon={Receipt} title="No invoices yet" /> : (
          <Table data-testid="invoice-table">
            <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead className="hidden md:table-cell">Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead>{manager && <TableHead />}</TableRow></TableHeader>
            <TableBody>
              {rows.map((i) => (
                <TableRow key={i.invoice_id} data-testid="invoice-row">
                  <TableCell className="font-mono text-xs whitespace-nowrap">{i.number}<p className="text-[10px] text-muted-foreground capitalize font-sans">{i.source}</p></TableCell>
                  <TableCell>{i.customer}</TableCell>
                  <TableCell className="hidden md:table-cell tabular-nums">{fmtDate(i.due_date)}</TableCell>
                  <TableCell><StatusBadge status={i.status} testid="invoice-status" /></TableCell>
                  <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">{money(i.amount, cur)}</TableCell>
                  {manager && <TableCell className="whitespace-nowrap text-right">
                    {i.status !== "paid" && <Button size="sm" variant="secondary" onClick={() => paid(i)} data-testid="invoice-mark-paid-button"><Check className="h-3.5 w-3.5 mr-1" />Paid</Button>}
                    <Button variant="ghost" size="icon" className="h-7 w-7 ml-1" onClick={() => del(i)} aria-label="Delete" data-testid="invoice-delete-button"><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle>New invoice</DialogTitle><DialogDescription>Records the invoice for tracking. It is not emailed to the customer.</DialogDescription></DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="space-y-1.5"><Label>Customer</Label><Input required value={f.customer} onChange={(e) => setF({ ...f, customer: e.target.value })} data-testid="invoice-customer-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount ({cur})</Label><Input required type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="invoice-amount-input" /></div>
              <div className="space-y-1.5"><Label>Status</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="sent">Sent</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1.5"><Label>Due date</Label><DatePicker value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} placeholder="In 30 days" allowClear testid="invoice-due-picker" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="invoice-description-input" /></div>
            <DialogFooter><Button type="submit" data-testid="invoice-save-button">Create invoice</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Budgets({ s, cur, manager, onChange }) {
  const [f, setF] = useState({ category: "", monthly_amount: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/finance/budgets", { category: f.category, monthly_amount: Number(f.monthly_amount) }); toast.success("Budget saved"); setF({ category: "", monthly_amount: "" }); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { try { await api.delete(`/finance/budgets/${id}`); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="lg:col-span-8 bg-card border border-border rounded-xl p-5" data-testid="budget-list">
        {s.budget_vs_actual.length === 0 ? <EmptyState icon={Wallet} title="No budgets yet" /> : (
          <ul className="space-y-4">
            {s.budget_vs_actual.map((b) => (
              <li key={b.category} data-testid="budget-row">
                <div className="flex items-center justify-between gap-2 text-sm mb-1.5">
                  <span className="font-medium">{b.category}</span>
                  <span className="flex items-center gap-2 tabular-nums text-muted-foreground">{money(b.actual, cur)} of {b.budget ? money(b.budget, cur) : "—"}
                    {manager && b.budget_id && <button onClick={() => del(b.budget_id)} aria-label="Remove budget" className="p-1 rounded hover:bg-muted"><Trash2 className="h-3.5 w-3.5" /></button>}
                    {manager && !b.budget_id && <button onClick={() => setF({ category: b.category, monthly_amount: "" })} className="text-xs text-primary hover:underline">Set budget</button>}</span>
                </div>
                <Progress value={b.budget ? Math.min(100, (b.actual / b.budget) * 100) : 0} className={`h-2 ${b.budget && b.actual > b.budget ? "[&>div]:bg-destructive" : ""}`} />
              </li>
            ))}
          </ul>
        )}
      </div>
      {manager && (
        <form onSubmit={save} className="lg:col-span-4 bg-card border border-border rounded-xl p-5 space-y-4 h-fit">
          <p className="text-sm font-semibold">Set a monthly budget</p>
          <div className="space-y-1.5"><Label>Category</Label><Input required value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="budget-category-input" /></div>
          <div className="space-y-1.5"><Label>Monthly amount ({cur})</Label><Input required type="number" min="0" step="1" value={f.monthly_amount} onChange={(e) => setF({ ...f, monthly_amount: e.target.value })} data-testid="budget-amount-input" /></div>
          <Button type="submit" className="w-full" data-testid="budget-save-button">Save budget</Button>
        </form>
      )}
    </div>
  );
}

function Connections({ manager, onChange }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get("/finance/integrations"); setList(data); } catch (e) { toast.error(errMsg(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const connect = async (p) => {
    setBusy(p.provider);
    try { const { data } = await api.post(`/finance/integrations/${p.provider}/connect`, { origin: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(errMsg(err)); setBusy(null); }
  };
  const sync = async (p) => {
    setBusy(p.provider);
    try { const { data } = await api.post(`/finance/integrations/${p.provider}/sync`); toast.success(`Synced ${data.invoices} invoices and ${data.expenses} expenses`); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(null); }
  };
  const disconnect = async (p) => { if (!window.confirm(`Disconnect ${p.name}? Imported records stay.`)) return; await api.delete(`/finance/integrations/${p.provider}`); load(); };
  if (!list) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {list.map((p) => (
        <div key={p.provider} className="bg-card border border-border rounded-xl p-5" data-testid={`connection-card-${p.provider}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center"><Link2 className="h-5 w-5" /></div>
              <div><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">Imports invoices, payments and expenses from the last 12 months</p></div></div>
            <span className={`text-xs rounded-full px-2.5 py-1 ${p.connected ? "bg-[hsl(var(--success))] text-white" : "bg-muted"}`} data-testid={`connection-status-${p.provider}`}>{p.connected ? "Connected" : "Not connected"}</span>
          </div>
          {p.connected ? (
            <>
              <p className="text-sm mt-4">{p.org_name}</p>
              <p className="text-xs text-muted-foreground">{p.last_sync ? `Last sync ${fmtDateTime(p.last_sync)} · ${p.last_sync_result?.invoices ?? 0} invoices, ${p.last_sync_result?.expenses ?? 0} expenses` : "Not synced yet"}</p>
              {manager && <div className="flex gap-2 mt-4">
                <Button size="sm" onClick={() => sync(p)} disabled={busy === p.provider} data-testid={`connection-sync-${p.provider}`}>{busy === p.provider ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}Sync now</Button>
                <Button size="sm" variant="ghost" onClick={() => disconnect(p)} data-testid={`connection-disconnect-${p.provider}`}><Unplug className="h-4 w-4 mr-1" />Disconnect</Button>
              </div>}
            </>
          ) : p.configured ? (
            manager ? <Button className="mt-4" onClick={() => connect(p)} disabled={busy === p.provider} data-testid={`connection-connect-${p.provider}`}>{busy === p.provider && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Connect {p.name}</Button>
              : <p className="text-xs text-muted-foreground mt-4">Ask a manager to connect.</p>
          ) : (
            <div className="mt-4 text-xs text-muted-foreground bg-muted rounded-lg p-3" data-testid={`connection-setup-needed-${p.provider}`}>
              The platform's {p.name} app keys haven't been added yet, so connecting isn't available. When they are, add this redirect address in the developer app:
              <p className="font-mono break-all mt-1 text-foreground">{(BACKEND_URL || window.location.origin)}/api/finance/integrations/{p.provider}/callback</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Cfo() {
  return (
    <UpgradeGate entitlement="ai_cfo">
      <CfoInner />
    </UpgradeGate>
  );
}

function CfoInner() {
  const { user } = useAuth();
  const manager = isManager(user);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [s, setS] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get("/finance/summary", { params: { today: localToday() } }); setS(data); } catch (e) { toast.error(errMsg(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const c = params.get("connected"), e = params.get("error");
    if (c) toast.success(`${c === "xero" ? "Xero" : "QuickBooks"} connected. Click Sync now to import.`);
    if (e) toast.error(`Couldn't connect ${e === "xero" ? "Xero" : "QuickBooks"}. Please try again.`);
    if (c || e) setParams({ tab: "connections" }, { replace: true });
  }, [params, setParams]);
  if (!s) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const cur = s.currency;
  return (
    <div data-testid="cfo-page">
      <PageHeader title="AI CFO" subtitle="Cash, runway, invoices and budgets, with a finance advisor that reads your real numbers" />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <div className="mb-4">
          <TabsList className="h-auto flex-wrap justify-start">
            {[["overview", "Overview"], ["transactions", "Transactions"], ["invoices", "Invoices"], ["budgets", "Budgets"], ["connections", "Connections"], ["chat", "Chat"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`cfo-tab-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="overview"><Overview s={s} reload={load} manager={manager} /></TabsContent>
        <TabsContent value="transactions"><Transactions cur={cur} manager={manager} onChange={load} /></TabsContent>
        <TabsContent value="invoices"><Invoices cur={cur} manager={manager} onChange={load} /></TabsContent>
        <TabsContent value="budgets"><Budgets s={s} cur={cur} manager={manager} onChange={load} /></TabsContent>
        <TabsContent value="connections"><Connections manager={manager} onChange={load} /></TabsContent>
        <TabsContent value="chat">
          <AdvisorChat role="cfo" className="h-[calc(100vh-15rem)] min-h-[520px]" persona={{
            name: "AI CFO", icon: Wallet, tagline: "Reads your cash, invoices and budgets. Proposes changes for your approval.",
            greeting: "Let's look at the numbers", intro: "Ask about cash, runway, margins, overdue invoices or budgets. Any invoice, transaction or budget change comes to you as a card to approve.",
            placeholder: "e.g. How many months of runway do we have?",
            suggestions: ["How healthy are our finances this month?", "What's our runway and how can we extend it?", "Which invoices should I chase first?",
              "Where are we over budget?", "Set sensible budgets based on the last 3 months", "Can we afford to hire a new designer?"],
          }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
